import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { updateCurrency, updateIncognito } from 'actions'
import capitalize from 'capitalize'
import Breadcrumbs from 'components/Breadcrumbs'
import Field from 'components/forms/Field'
import LoadingIcon from 'components/LoadingIcon'
import {
  addSharedUser,
  createCustomField,
  deleteBankroll,
  deleteCustomField,
  exportExpenses,
  exportSessions,
  removeSharedUser,
  updateBankroll,
  updateCustomField,
} from 'config/api'
import routes from 'config/routes'
import { debounce } from 'debounce'
import { saveAs } from 'file-saver'
import { Form as FormikForm, Formik } from 'formik'
import { useBankrolls } from 'hooks'
import PropTypes from 'prop-types'
import React from 'react'
import {
  Alert,
  Button,
  Card,
  Col,
  Form,
  InputGroup,
  Modal,
  Row,
} from 'react-bootstrap'
import { useMutation, useQueryClient } from 'react-query'
import { connect } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { useToasts } from 'react-toast-notifications'
import { confirm, currencies, sleep } from 'utils'
import BankrollFilter from 'components/BankrollFilter'
import * as Yup from 'yup'

const schema = Yup.object().shape({
  username: Yup.string().required('Required'),
})

function Settings({
  currency,
  incognito,
  owner,
  updateCurrency,
  updateIncognito,
}) {
  const { addToast } = useToasts()
  const history = useHistory()
  const { icon } = currencies.find(c => c.name === currency) || currencies[0]
  const { currentBankroll, bankrolls, selectBankroll, createBankroll } =
    useBankrolls()
  const [exporting, setExporting] = React.useState(null)
  const [deleting, setDeleting] = React.useState(null)
  const [editingCustomFields, setEditingCustomFields] = React.useState(false)
  const [showAddUserModal, setShowAddUserModal] = React.useState(false)
  const [addingUser, setAddingUser] = React.useState(false)
  const [addUserError, setAddUserError] = React.useState()
  const qClient = useQueryClient()

  const mBankroll = useMutation(
    bankroll => {
      return updateBankroll(currentBankroll.id, bankroll)
    },
    {
      onSuccess: () => {
        qClient.invalidateQueries('bankrolls')
      },
    },
  )

  const mCustomField = useMutation(
    ({ action, id, ...field }) => {
      switch (action) {
        case 'create':
          return createCustomField(currentBankroll.id, field)
        case 'update':
          return updateCustomField(currentBankroll.id, id, field)
        case 'delete':
          return deleteCustomField(currentBankroll.id, id)
        default:
          throw new Error('unknown action')
      }
    },
    {
      onSuccess: () => {
        qClient.invalidateQueries('bankrolls')
      },
    },
  )

  const mDeleteBankroll = useMutation(deleteBankroll, {
    onSuccess: (data, variables) => {
      // go to the next bankroll
      selectBankroll(bankrolls.find(p => p.id !== variables))
      history.push(routes.dashboard)
    },
  })

  const toggleModal = () => setShowAddUserModal(!showAddUserModal)

  const handleCurrency = async event => {
    try {
      await updateCurrency(event.target.value)
      addToast('Updated currency settings', {
        appearance: 'success',
        autoDismiss: true,
      })
    } catch (err) {
      addToast('Error updating currency settings', {
        appearance: 'error',
        autoDismiss: true,
      })
    }
  }

  const handleIncognito = async event => {
    try {
      await updateIncognito(event.target.checked)
      addToast('Updated incognito settings', {
        appearance: 'success',
        autoDismiss: true,
      })
    } catch (err) {
      addToast('Error updating incognito settings', {
        appearance: 'error',
        autoDismiss: true,
      })
    }
  }

  const handleBankrollTitle = debounce(async title => {
    try {
      await mBankroll.mutateAsync({ title })
      addToast('Updated bankroll title', {
        appearance: 'success',
        autoDismiss: true,
      })
    } catch (err) {
      addToast('Error updating bankroll title', {
        appearance: 'error',
        autoDismiss: true,
      })
    }
  }, 1000)

  const handleExpenses = async event => {
    try {
      await mBankroll.mutateAsync({ include_expenses: event.target.checked })
      addToast('Updated expense settings', {
        appearance: 'success',
        autoDismiss: true,
      })
    } catch (err) {
      addToast('Error updating expense settings', {
        appearance: 'error',
        autoDismiss: true,
      })
    }
  }

  const handleEvDisabled = async event => {
    try {
      await mBankroll.mutateAsync({ ev_disabled: event.target.checked })
      addToast('Updated EV settings', {
        appearance: 'success',
        autoDismiss: true,
      })
    } catch (err) {
      addToast('Error updating EV settings', {
        appearance: 'error',
        autoDismiss: true,
      })
    }
  }

  const handleSessionExport = async () => {
    try {
      setExporting('sessions')
      const [data] = await Promise.all([
        exportSessions(currentBankroll.id),
        sleep(1000),
      ])
      saveAs(data, 'export-sessions.csv')
    } catch (err) {
      addToast('Error exporting session data', {
        appearance: 'error',
        autoDismiss: true,
      })
    } finally {
      setExporting(null)
    }
  }

  const handleExpenseExport = async () => {
    try {
      setExporting('expenses')
      const [data] = await Promise.all([
        exportExpenses(currentBankroll.id),
        sleep(1000),
      ])
      saveAs(data, 'export-expenses.csv')
    } catch (err) {
      addToast('Error exporting expense data', {
        appearance: 'error',
        autoDismiss: true,
      })
    } finally {
      setExporting(null)
    }
  }

  const handleNewField = async field => {
    try {
      setEditingCustomFields(`adding ${field.model}`)
      await sleep(300)
      await mCustomField.mutateAsync({ ...field, action: 'create' })
      addToast('Created custom field', {
        appearance: 'success',
        autoDismiss: true,
      })
    } catch (err) {
      addToast('Error creating custom field', {
        appearance: 'error',
        autoDismiss: true,
      })
    } finally {
      setEditingCustomFields(false)
    }
  }

  const handleUpdateField = debounce(async field => {
    try {
      await mCustomField.mutateAsync({ ...field, action: 'update' })
      addToast('Updated custom field', {
        appearance: 'success',
        autoDismiss: true,
      })
    } catch (err) {
      addToast('Error updating custom field', {
        appearance: 'error',
        autoDismiss: true,
      })
    }
  }, 1000)

  const handleDeleteField = async field => {
    const msg = `Are you sure you want to delete the custom field "${field.title}"? This action cannot be undone and the custom field data will be removed permanently.`
    try {
      await confirm(msg)
      try {
        setEditingCustomFields(`deleting ${field.id}`)
        await mCustomField.mutateAsync({ ...field, action: 'delete' })
        addToast('Deleted custom field', {
          appearance: 'success',
          autoDismiss: true,
        })
      } catch (err) {
        addToast('Error deleting custom field', {
          appearance: 'error',
          autoDismiss: true,
        })
      } finally {
        setEditingCustomFields(false)
      }
    } catch (err) {
      // no-op
    }
  }

  const _deleteBankroll = async () => {
    // Prevent users from deleting their only bankroll
    if (bankrolls.length <= 1) {
      addToast('Please create a new bankroll before deleting this one.', {
        appearance: 'error',
        autoDismiss: true,
      })
      return
    }

    try {
      setDeleting(true)
      await confirm(
        'Are you sure you want to delete this bankroll? This action cannot be undone and the bankroll data will be removed permanently.',
      )
      await mDeleteBankroll.mutateAsync(currentBankroll.id)
    } catch (err) {
      setDeleting(false)
    }
  }

  const _addUserToBankroll = async ({ username }) => {
    try {
      setAddingUser(true)
      await addSharedUser(currentBankroll.id, username)
      qClient.invalidateQueries('bankrolls')
      toggleModal()
      addToast(`Added \`${username}\` to bankroll`, {
        appearance: 'success',
        autoDismiss: true,
      })
    } catch (err) {
      setAddUserError(
        err?.response?.status === 400
          ? `Unable to add \`${username}\`. Please check the username and try again.`
          : 'An unknown error occurred, please refresh the page and try again.',
      )
    } finally {
      setAddingUser(false)
    }
  }

  const _removeUserFromBankroll = async username => {
    try {
      await confirm(
        `Are you sure you want to remove \`${username}\` from this bankroll?`,
      )
      await removeSharedUser(currentBankroll.id, username)
      qClient.invalidateQueries('bankrolls')
      addToast(`Removed \`${username}\` from bankroll`, {
        appearance: 'success',
        autoDismiss: true,
      })
    } catch (err) {
      addToast(`Error removing \`${username}\` from bankroll`, {
        appearance: 'error',
        autoDismiss: true,
      })
    }
  }

  return (
    <>
      <div id="settings">
        <Breadcrumbs
          crumbs={[
            ['DASHBOARD', routes.dashboard],
            ['SETTINGS', routes.settings],
          ]}
        />
        <Row>
          <Col className="d-flex flex-wrap justify-content-start justify-content-md-between">
            <BankrollFilter
              current={currentBankroll}
              choices={bankrolls}
              onChange={selectBankroll}
              onCreate={createBankroll}
            />
          </Col>
        </Row>
        <Row>
          <Col className="mt-3">
            <Card className="shadow">
              <Card.Body>
                <Card.Title>SETTINGS</Card.Title>
                <Row className="mt-3 justify-content-between">
                  <Col md={6} lg={4}>
                    <Form.Group>
                      <Form.Label className="font-weight-bold">
                        CURRENCY
                      </Form.Label>
                      <InputGroup>
                        <InputGroup.Prepend>
                          <InputGroup.Text>
                            <FontAwesomeIcon icon={icon} />
                          </InputGroup.Text>
                        </InputGroup.Prepend>
                        <Form.Control
                          as="select"
                          value={currency}
                          onChange={handleCurrency}
                          custom
                        >
                          {currencies.map(({ name }) => (
                            <option key={name} value={name}>
                              {capitalize(name)}
                            </option>
                          ))}
                        </Form.Control>
                      </InputGroup>
                      <small className="form-text text-muted">
                        Select which currency symbol should be used throughout
                        the site.
                      </small>
                    </Form.Group>
                  </Col>
                  <Col md={6} lg={5}>
                    <Form.Group>
                      <p className="font-weight-bold form-label mb-2">
                        INCOGNITO
                      </p>
                      <Form.Switch
                        label="Enable incognito mode"
                        id="incognito-switch"
                        size="lg"
                        checked={incognito}
                        onChange={handleIncognito}
                      />
                      <small className="form-text text-muted">
                        Incognito mode hides the Blackjack Apprenticeship logo
                        just in case somebody is looking over your shoulder and
                        you&apos;re trying to keep your AP status on the DL.
                      </small>
                    </Form.Group>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>
        </Row>
        <Row>
          <Col className="mt-3">
            <Card className="shadow">
              <Card.Body>
                <Card.Title>BANKROLL SETTINGS</Card.Title>
                <Row className="mt-3 justify-content-between">
                  <Col md={6} lg={4}>
                    <Form.Group>
                      <Form.Label className="font-weight-bold">NAME</Form.Label>
                      <InputGroup>
                        <Form.Control
                          defaultValue={currentBankroll.title}
                          onChange={e => handleBankrollTitle(e.target.value)}
                        />
                      </InputGroup>
                      <small className="form-text text-muted">
                        Name your bankroll – helpful if you have multiple
                        bankrolls.
                      </small>
                    </Form.Group>
                    <Form.Group>
                      <p className="font-weight-bold form-label mb-2">
                        EXPENSES
                      </p>
                      <Form.Switch
                        label="Include expenses"
                        id="expenses-switch"
                        size="lg"
                        checked={currentBankroll.include_expenses}
                        onChange={handleExpenses}
                      />
                      <small className="form-text text-muted">
                        Enabling this setting will adjust your Total Earnings
                        and AV on the dashboard to reflect net earnings.
                      </small>
                    </Form.Group>
                    <Form.Group>
                      <p className="font-weight-bold form-label mb-2">EV</p>
                      <Form.Switch
                        label="Disable EV"
                        id="disable-ev-switch"
                        size="lg"
                        checked={currentBankroll.ev_disabled}
                        onChange={handleEvDisabled}
                      />
                      <small className="form-text text-muted">
                        If you don&apos;t care about tracking EV, go ahead and
                        disable it and simply track AV instead.
                      </small>
                    </Form.Group>
                    <Form.Group>
                      <p className="font-weight-bold form-label mb-2">
                        EXPORT DATA
                      </p>
                      <Button
                        variant="warning"
                        onClick={handleSessionExport}
                        disabled={exporting}
                        block
                      >
                        {exporting === 'sessions' ? (
                          <LoadingIcon size="xs" />
                        ) : (
                          <>
                            <FontAwesomeIcon
                              icon="cloud-download-alt"
                              className="mr-1"
                              fixedWidth
                            />
                            DOWNLOAD SESSIONS
                          </>
                        )}
                      </Button>
                      <Button
                        variant="warning"
                        onClick={handleExpenseExport}
                        disabled={exporting}
                        className="mt-1"
                        block
                      >
                        {exporting === 'expenses' ? (
                          <LoadingIcon size="xs" />
                        ) : (
                          <>
                            <FontAwesomeIcon
                              icon="cloud-download-alt"
                              className="mr-1"
                              fixedWidth
                            />
                            DOWNLOAD EXPENSES
                          </>
                        )}
                      </Button>
                      <small className="form-text text-muted">
                        Export your data at anytime in CSV format for extended
                        analysis.
                      </small>
                    </Form.Group>
                  </Col>
                  <Col md={6} lg={5}>
                    <Form.Group>
                      <Form.Label className="font-weight-bold">
                        CUSTOM SESSION FIELDS
                      </Form.Label>
                      <p className="text-muted small">
                        Add custom fields to track additional session
                        information that may be useful to you.
                      </p>
                      {currentBankroll.custom_session_fields.map(field => (
                        <Form.Group key={field.id}>
                          <InputGroup>
                            <Form.Control
                              defaultValue={field.title}
                              onChange={e =>
                                handleUpdateField({
                                  ...field,
                                  title: e.target.value,
                                })
                              }
                            />
                            <InputGroup.Append>
                              <Button
                                variant="danger"
                                disabled={editingCustomFields}
                                onClick={() => handleDeleteField(field)}
                              >
                                <FontAwesomeIcon icon="times" />
                              </Button>
                            </InputGroup.Append>
                          </InputGroup>
                        </Form.Group>
                      ))}
                      <Button
                        variant="warning"
                        size="sm"
                        disabled={editingCustomFields}
                        onClick={() =>
                          handleNewField({
                            model: 'session',
                            title: 'New Field',
                          })
                        }
                      >
                        {editingCustomFields === 'adding session' ? (
                          <LoadingIcon size="xs" />
                        ) : (
                          <>
                            <FontAwesomeIcon
                              icon={['far', 'plus']}
                              className="mr-1"
                              fixedWidth
                            />
                            ADD FIELD
                          </>
                        )}
                      </Button>
                    </Form.Group>
                    <Form.Group>
                      <Form.Label className="font-weight-bold">
                        CUSTOM EXPENSE FIELDS
                      </Form.Label>
                      <p className="text-muted small">
                        Add custom fields to track additional expense
                        information that may be useful to you.
                      </p>
                      {currentBankroll.custom_expense_fields.map(field => (
                        <Form.Group key={field.id}>
                          <InputGroup>
                            <Form.Control
                              defaultValue={field.title}
                              onChange={e =>
                                handleUpdateField({
                                  ...field,
                                  title: e.target.value,
                                })
                              }
                            />
                            <InputGroup.Append>
                              <Button
                                variant="danger"
                                disabled={editingCustomFields}
                                onClick={() => handleDeleteField(field)}
                              >
                                <FontAwesomeIcon icon="times" />
                              </Button>
                            </InputGroup.Append>
                          </InputGroup>
                        </Form.Group>
                      ))}
                      <Button
                        variant="warning"
                        size="sm"
                        disabled={editingCustomFields}
                        onClick={() =>
                          handleNewField({
                            model: 'expense',
                            title: 'New Field',
                          })
                        }
                      >
                        {editingCustomFields === 'adding expense' ? (
                          <LoadingIcon size="xs" />
                        ) : (
                          <>
                            <FontAwesomeIcon
                              icon={['far', 'plus']}
                              className="mr-1"
                              fixedWidth
                            />
                            ADD FIELD
                          </>
                        )}
                      </Button>
                    </Form.Group>
                  </Col>
                </Row>
              </Card.Body>
              {owner === currentBankroll.user && (
                <>
                  <hr />
                  <Card.Body>
                    <Card.Title className="text-danger">DANGER ZONE</Card.Title>
                    <Row className="mt-3 justify-content-between">
                      <Col md={6} lg={4}>
                        <Form.Group>
                          <p className="font-weight-bold form-label mb-2">
                            DELETE BANKROLL
                          </p>
                          <p className="text-muted small">
                            Once you delete a bankroll, there is no going back.
                            We recommend exporting any sessions and expenses
                            before deleting your bankroll.
                          </p>
                          <Button
                            variant="danger"
                            onClick={_deleteBankroll}
                            disabled={deleting}
                            block
                          >
                            <FontAwesomeIcon
                              icon="fa-regular fa-trash"
                              className="mr-1"
                              fixedWidth
                            />
                            DELETE BANKROLL
                          </Button>
                        </Form.Group>
                      </Col>
                      <Col md={6} lg={5}>
                        <Form.Group>
                          <Form.Label className="font-weight-bold">
                            SHARING
                          </Form.Label>
                          <p className="text-muted small">
                            You can share access to your bankroll with other BJA
                            users. This is great for teams using a shared
                            bankroll.
                          </p>
                          <Alert variant="warning" className="small">
                            <strong>IMPORTANT:</strong> Only you, the owner of
                            the bankroll, can manage other users who have
                            access, but all other users will be able to see and
                            manage all bankroll data.
                          </Alert>
                          {currentBankroll.shared_users.map(username => (
                            <Form.Group key={username}>
                              <InputGroup>
                                <Form.Control value={username} readOnly />
                                <InputGroup.Append>
                                  <Button
                                    variant="danger"
                                    onClick={() =>
                                      _removeUserFromBankroll(username)
                                    }
                                  >
                                    <FontAwesomeIcon icon="times" />
                                  </Button>
                                </InputGroup.Append>
                              </InputGroup>
                            </Form.Group>
                          ))}
                          <Button
                            variant="warning"
                            size="sm"
                            disabled={showAddUserModal}
                            onClick={toggleModal}
                          >
                            <>
                              <FontAwesomeIcon
                                icon={['far', 'plus']}
                                className="mr-1"
                                fixedWidth
                              />
                              ADD USER
                            </>
                          </Button>
                        </Form.Group>
                      </Col>
                    </Row>
                  </Card.Body>
                </>
              )}
            </Card>
          </Col>
        </Row>
      </div>
      <Modal show={showAddUserModal} onHide={toggleModal}>
        <Modal.Header
          closeButton
          className="border-0 text-success font-weight-bold"
        >
          ADD USER
        </Modal.Header>
        <Formik
          onSubmit={_addUserToBankroll}
          validationSchema={schema}
          initialValues={{
            username: '',
          }}
        >
          {() => (
            <FormikForm>
              <Modal.Body>
                <Field label="Username" name="username" />
                {addUserError && (
                  <Form.Text className="text-danger">{addUserError}</Form.Text>
                )}
              </Modal.Body>
              <Modal.Footer className="border-0">
                <Button
                  variant="secondary"
                  disabled={addingUser}
                  onClick={toggleModal}
                >
                  Cancel
                </Button>
                <Button variant="warning" disabled={addingUser} type="submit">
                  {addingUser ? <LoadingIcon size="xs" /> : 'Add'}
                </Button>
              </Modal.Footer>
            </FormikForm>
          )}
        </Formik>
      </Modal>
    </>
  )
}

Settings.propTypes = {
  currency: PropTypes.string.isRequired,
  incognito: PropTypes.bool.isRequired,
  owner: PropTypes.string.isRequired,
  updateCurrency: PropTypes.func.isRequired,
  updateIncognito: PropTypes.func.isRequired,
}

const mapState = state => ({
  currency: state.settings.currency,
  incognito: state.settings.incognito,
  owner: state.auth.id,
})

const mapDispatch = {
  updateCurrency,
  updateIncognito,
}

export default connect(mapState, mapDispatch)(Settings)
